"use client";

import "swiper/css";

import { useEffect, useRef, useState } from "react";

import Link from "next/link";

import cn from "classnames";
import type { HTMLAttributes, ReactNode } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import { CarouselCenteredNavButton } from "./carouselCenteredNavButton.component";
import { CarouselCenteredProgress } from "./carouselCenteredProgress.component";

export const CarouselCentered = ({
  children,
  ctaLabel,
  ctaHref,
  ctaOpenInNewTab,
  ctaFilled,
  ctaClassName,
  className,
  swiperItemsClassName,
  slideInnerClassName,
  mobileSlidesPerView = 1.25,
  desktopSlidesPerView = 1.65,
  mobileSpaceBetween = 20,
  desktopSpaceBetween = 28,
  mobileSlidesPerGroup = 1,
  desktopSlidesPerGroup,
  centeredSlides = true,
  desktopCenteredSlides,
  initialSlide = 1,
  showNavigation = true,
  equalSlideHeights = false,
  showPagedProgress = false,
  slidesFooter,
  ...rest
}: CarouselCenteredProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [snapIndex, setSnapIndex] = useState(0);
  const [totalSnaps, setTotalSnaps] = useState(0);
  const [nativeScroll, setNativeScroll] = useState(false);
  const swiperRef = useRef<SwiperType | null>(null);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia("(max-width: 1023px)");
    const update = () => setNativeScroll(Boolean(media.matches));
    update();
    if (typeof media.addEventListener !== "function") return;
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  const slides = Array.isArray(children) ? children : [children];
  const totalSlides = slides.length;
  const showCta = Boolean(ctaLabel?.trim());
  const centeredSlidesDesktop = desktopCenteredSlides ?? centeredSlides;
  const slidesPerGroupDesktop = desktopSlidesPerGroup ?? mobileSlidesPerGroup;

  const syncSnapState = (swiper: SwiperType) => {
    setActiveIndex(swiper.realIndex);
    setSnapIndex(swiper.snapIndex);
    setTotalSnaps(swiper.snapGrid.length);
  };

  const breakpoints = {
    320: {
      slidesPerView: mobileSlidesPerView,
      spaceBetween: mobileSpaceBetween,
      slidesPerGroup: mobileSlidesPerGroup,
      centeredSlides,
    },
    1024: {
      slidesPerView: desktopSlidesPerView,
      spaceBetween: desktopSpaceBetween,
      slidesPerGroup: slidesPerGroupDesktop,
      centeredSlides: centeredSlidesDesktop,
    },
  };

  if (totalSlides === 0) return null;

  const progressTotal = Math.max(totalSnaps, 1);
  const progressIndex = Math.min(snapIndex, progressTotal - 1);
  // Hide arrows/progress when everything fits on one page (e.g. 3 cards at
  // desktopSlidesPerView={3}). Still show mobile nav when more snaps exist.
  const canNavigate = showNavigation && totalSnaps > 1;
  // Slide count, not snap state: snaps are 0 on the first render, and locking
  // touch then leaves the carousel stuck. Phone widths use native scroll so a
  // finger can swipe or scroll the track.
  const canSwipe = totalSlides > 1;
  const useNativeScroll = nativeScroll && canSwipe;

  return (
    <div className={cn("relative w-full", className)} {...rest}>
      <div className="w-full pb-1">
        <Swiper
          key={useNativeScroll ? "native" : "transform"}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            syncSnapState(swiper);
          }}
          onSlideChange={syncSnapState}
          onBreakpoint={syncSnapState}
          onResize={syncSnapState}
          onScroll={syncSnapState}
          allowTouchMove={canSwipe}
          cssMode={useNativeScroll}
          autoHeight={false}
          centeredSlides={centeredSlides}
          spaceBetween={mobileSpaceBetween}
          slidesPerGroup={mobileSlidesPerGroup}
          initialSlide={initialSlide}
          breakpoints={breakpoints}
          className={cn(
            "w-full! overflow-hidden!",
            useNativeScroll &&
              "touch-[pan-x_pan-y]! [&_.swiper-wrapper]:touch-[pan-x_pan-y]!",
            equalSlideHeights &&
              "[&_.swiper-slide]:box-border [&_.swiper-slide]:flex! [&_.swiper-slide]:h-auto! [&_.swiper-slide]:self-stretch [&_.swiper-wrapper]:items-stretch",
          )}>
          {slides.map((slide, index) => (
            <SwiperSlide
              key={index}
              className={cn(
                "h-auto",
                equalSlideHeights && "flex! self-stretch",
                swiperItemsClassName,
              )}>
              <div
                className={cn(
                  "h-full w-full",
                  equalSlideHeights && "flex min-h-full flex-1 flex-col",
                  slideInnerClassName,
                )}
                onClickCapture={(e) => {
                  // Non-centered tracks show several full slides (resources,
                  // featured kits). Let those clicks follow links instead of
                  // treating every non-active card as a peek-to-advance target.
                  // Prefer the active breakpoint value so desktop can opt out
                  // of centered behavior while mobile stays centered.
                  const s = swiperRef.current;
                  if (!s?.params.centeredSlides) return;

                  // If the slide is already active or the click event is not a left click, do nothing
                  if (index === activeIndex || e.button !== 0) return;

                  // If the click event is a meta, ctrl, shift, or alt key, do nothing
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                  e.preventDefault();
                  e.stopPropagation();

                  // Navigate to the slide
                  if (index < activeIndex) {
                    s.slidePrev();
                  } else {
                    s.slideNext();
                  }
                }}>
                {slide}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {slidesFooter}

      {(canNavigate || showCta) && (
        <div className="mt-6 flex flex-col items-center gap-4 px-4 lg:hidden">
          {canNavigate ? (
            <div className="flex w-full items-center justify-center gap-6">
              <CarouselCenteredNavButton
                swiperRef={swiperRef}
                direction="prev"
              />
              <CarouselCenteredProgress
                activeIndex={progressIndex}
                totalSlides={progressTotal}
                swiperRef={swiperRef}
              />
              <CarouselCenteredNavButton
                swiperRef={swiperRef}
                direction="next"
              />
            </div>
          ) : null}
          {showCta ? (
            <Link
              data-testid="carousel-centered-cta"
              href={ctaHref ?? ""}
              target={ctaOpenInNewTab ? "_blank" : undefined}
              rel={ctaOpenInNewTab ? "noopener noreferrer" : undefined}
              className={cn(
                "inline-flex min-h-11 min-w-11 items-center justify-center rounded-full px-6 py-2 text-sm font-bold uppercase transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
                {
                  "border-secondary text-secondary hover:bg-secondary focus-visible:ring-secondary border-2 hover:text-white":
                    !ctaFilled,
                  "bg-secondary hover:bg-secondary-300 hover:border-secondary-100 focus-visible:ring-secondary border border-transparent text-white":
                    ctaFilled,
                },
                "mt-2",
                ctaClassName,
              )}>
              {ctaLabel}
            </Link>
          ) : null}
        </div>
      )}

      {(canNavigate || showCta) && (
        <div className="mt-6 hidden grid-cols-3 items-center px-4 lg:grid">
          <div className="shrink-0">
            {showCta ? (
              <Link
                data-testid="carousel-centered-cta"
                href={ctaHref ?? ""}
                target={ctaOpenInNewTab ? "_blank" : undefined}
                rel={ctaOpenInNewTab ? "noopener noreferrer" : undefined}
                className={cn(
                  "inline-flex min-h-11 min-w-11 items-center justify-center rounded-full px-6 py-2 text-sm font-bold uppercase transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
                  {
                    "border-secondary text-secondary hover:bg-secondary focus-visible:ring-secondary border-2 hover:text-white":
                      !ctaFilled,
                    "bg-secondary hover:bg-secondary-300 hover:border-secondary-100 focus-visible:ring-secondary border border-transparent text-white":
                      ctaFilled,
                  },
                  "lg:min-h-12 lg:px-8 lg:py-3",
                  ctaClassName,
                )}>
                {ctaLabel}
              </Link>
            ) : null}
          </div>

          {canNavigate ? (
            <>
              <div className="flex flex-1 justify-center">
                <CarouselCenteredProgress
                  activeIndex={progressIndex}
                  totalSlides={progressTotal}
                  swiperRef={swiperRef}
                  showPagedProgress={showPagedProgress}
                />
              </div>

              <div className="flex shrink-0 justify-end gap-4">
                <CarouselCenteredNavButton
                  swiperRef={swiperRef}
                  direction="prev"
                />
                <CarouselCenteredNavButton
                  swiperRef={swiperRef}
                  direction="next"
                />
              </div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};

export type CarouselCenteredProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode | ReactNode[];
  ctaLabel?: string;
  ctaHref?: string;
  ctaOpenInNewTab?: boolean;
  ctaFilled?: boolean;
  ctaClassName?: string;
  className?: string;
  swiperItemsClassName?: string;
  slideInnerClassName?: string;
  mobileSlidesPerView?: number;
  desktopSlidesPerView?: number;
  mobileSpaceBetween?: number;
  desktopSpaceBetween?: number;
  mobileSlidesPerGroup?: number;
  desktopSlidesPerGroup?: number;
  centeredSlides?: boolean;
  /** Overrides `centeredSlides` at the desktop (1024px) breakpoint. */
  desktopCenteredSlides?: boolean;
  initialSlide?: number;
  showNavigation?: boolean;
  equalSlideHeights?: boolean;
  showPagedProgress?: boolean;
  /** Rendered between the slides and the navigation controls. */
  slidesFooter?: ReactNode;
};
